import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout.jsx';
import client from '../api/client.js';
import { useAuthStore } from '../store/auth.js';

const styles = {
  container: {
    padding: '0',
  },
  image: {
    width: '100%',
    maxHeight: '280px',
    objectFit: 'cover',
  },
  imagePlaceholder: {
    width: '100%',
    height: '140px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFF5F0',
    fontSize: '64px',
  },
  body: {
    padding: '16px',
  },
  title: {
    fontSize: '20px',
    fontWeight: '700',
    color: 'var(--tg-theme-text-color, #000)',
    marginBottom: '8px',
    lineHeight: 1.3,
  },
  urlLink: {
    display: 'block',
    fontSize: '13px',
    color: 'var(--tg-theme-link-color, #007aff)',
    marginBottom: '12px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textDecoration: 'none',
  },
  description: {
    fontSize: '14px',
    color: 'var(--tg-theme-hint-color, #8e8e93)',
    lineHeight: 1.5,
    marginBottom: '16px',
  },
  section: {
    marginBottom: '16px',
  },
  sectionTitle: {
    fontSize: '12px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: 'var(--tg-theme-hint-color, #8e8e93)',
    marginBottom: '8px',
  },
  notes: {
    backgroundColor: '#FFF5F0',
    borderRadius: '10px',
    padding: '12px',
    fontSize: '14px',
    color: 'var(--tg-theme-text-color, #000)',
    lineHeight: 1.5,
  },
  tags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
  },
  tag: {
    backgroundColor: '#FFF5F0',
    borderRadius: '8px',
    padding: '4px 10px',
    fontSize: '13px',
    color: 'var(--tg-theme-hint-color, #8e8e93)',
  },
  marks: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  mark: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    backgroundColor: '#FFF5F0',
    borderRadius: '8px',
    padding: '6px 10px',
    fontSize: '14px',
  },
  markBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '7px 10px',
    borderRadius: '10px',
    border: '1px solid var(--tg-theme-hint-color, #ddd)',
    backgroundColor: 'transparent',
    fontSize: '13px',
    cursor: 'pointer',
    color: 'var(--tg-theme-text-color, #000)',
    transition: 'all 0.15s ease',
    flex: '1 1 auto',
    minWidth: 0,
    justifyContent: 'center',
  },
  markBtnActive: {
    backgroundColor: 'var(--tg-theme-button-color, #007aff)',
    color: 'var(--tg-theme-button-text-color, #fff)',
    borderColor: 'var(--tg-theme-button-color, #007aff)',
  },
  actions: {
    display: 'flex',
    gap: '10px',
    padding: '0 16px 16px',
    flexWrap: 'wrap',
  },
  openBtn: {
    flex: 2,
    padding: '13px',
    backgroundColor: 'var(--tg-theme-button-color, #007aff)',
    color: 'var(--tg-theme-button-text-color, #fff)',
    border: 'none',
    borderRadius: '10px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    textDecoration: 'none',
    textAlign: 'center',
  },
  archiveBtn: {
    flex: 1,
    padding: '13px',
    backgroundColor: 'transparent',
    border: '1px solid var(--tg-theme-hint-color, #ccc)',
    borderRadius: '10px',
    fontSize: '15px',
    cursor: 'pointer',
    color: 'var(--tg-theme-text-color, #000)',
  },
  deleteBtn: {
    width: 'calc(100% - 32px)',
    padding: '13px',
    backgroundColor: 'transparent',
    border: '1px solid #ff3b30',
    borderRadius: '10px',
    fontSize: '15px',
    cursor: 'pointer',
    color: '#ff3b30',
    margin: '0 16px 16px',
    boxSizing: 'border-box',
  },
  meta: {
    fontSize: '12px',
    color: 'var(--tg-theme-hint-color, #8e8e93)',
    borderTop: '1px solid var(--tg-theme-hint-color, #e0e0e0)',
    padding: '12px 16px',
  },
  loadingText: {
    textAlign: 'center',
    padding: '48px',
    color: 'var(--tg-theme-hint-color, #8e8e93)',
  },
};

function PriceField({ item, itemId, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');

  const handleSave = async () => {
    setEditing(false);
    if (value.trim() === (item.price || '')) return;
    try {
      const res = await client.patch(`/items/${itemId}`, { price: value.trim() || null });
      onUpdate(res.data.price);
    } catch {}
  };

  if (editing) {
    return (
      <input
        autoFocus
        style={{ padding: '8px 12px', borderRadius: '10px', border: '1px solid #F4956A', fontSize: '15px', backgroundColor: '#FFF5F0', outline: 'none', width: '160px', boxSizing: 'border-box' }}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setEditing(false); }}
        onBlur={handleSave}
        maxLength={100}
        placeholder="1 500 ₽"
      />
    );
  }

  return (
    <div
      style={{ display: 'inline-block', padding: '8px 12px', borderRadius: '10px', backgroundColor: '#FFF5F0', fontSize: '15px', fontWeight: '600', color: '#F05A35', cursor: 'text', minWidth: '80px' }}
      onClick={() => { setValue(item.price || ''); setEditing(true); }}
    >
      {item.price || <span style={{ color: 'var(--tg-theme-hint-color, #8e8e93)', fontWeight: 400 }}>Добавить цену...</span>}
    </div>
  );
}

export default function ItemDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();
  const backListId = searchParams.get('list_id');
  const backCategoryId = searchParams.get('category_id');
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [markTypes, setMarkTypes] = useState([]);
  const [userMarks, setUserMarks] = useState({});
  const [editingTitle, setEditingTitle] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [titleValue, setTitleValue] = useState('');
  const [notesValue, setNotesValue] = useState('');
  const [categories, setCategories] = useState([]);
  const [movingCategory, setMovingCategory] = useState(false);
  const [canEdit, setCanEdit] = useState(false);

  useEffect(() => {
    const fetchItem = async () => {
      try {
        const response = await client.get(`/items/${id}`);
        setItem(response.data);
      } catch (err) {
        setError(err.response?.data?.error || 'Не удалось загрузить');
      } finally {
        setLoading(false);
      }
    };
    fetchItem();
  }, [id]);

  useEffect(() => {
    if (!item?.list_id) return;
    client.get(`/mark-types?list_id=${item.list_id}`)
      .then((res) => setMarkTypes(res.data))
      .catch(() => {});
    client.get(`/categories?list_id=${item.list_id}`)
      .then((res) => setCategories(res.data))
      .catch(() => {});
    client.get(`/lists/${item.list_id}`)
      .then((res) => {
        const list = res.data;
        const memberships = list.my_memberships || [];
        const edit = list.my_role === 'admin' ||
          memberships.some((m) => (m.role === 'edit' || m.role === 'admin') && (!m.category_id || m.category_id === item.category_id));
        setCanEdit(edit);
      })
      .catch(() => {});
    client.get(`/items/${id}/marks`)
      .then((res) => {
        const map = {};
        res.data.forEach((m) => { map[m.mark_type_id] = m.id; });
        setUserMarks(map);
      })
      .catch(() => {});
  }, [item?.list_id]);

  const handleSaveTitle = async () => {
    setEditingTitle(false);
    if (titleValue.trim() === (item.title || '')) return;
    try {
      const res = await client.patch(`/items/${id}`, { title: titleValue.trim() || null });
      setItem((prev) => ({ ...prev, title: res.data.title }));
    } catch {}
  };

  const handleSaveNotes = async () => {
    setEditingNotes(false);
    if (notesValue.trim() === (item.notes || '')) return;
    try {
      const res = await client.patch(`/items/${id}`, { notes: notesValue.trim() || null });
      setItem((prev) => ({ ...prev, notes: res.data.notes }));
    } catch {}
  };

  const handleMoveCategory = async (newCategoryId) => {
    setMovingCategory(false);
    if (newCategoryId === item.category_id) return;
    try {
      const res = await client.patch(`/items/${id}`, { category_id: newCategoryId });
      setItem((prev) => ({ ...prev, category_id: res.data.category_id }));
    } catch {
      alert('Не удалось переместить');
    }
  };

  const handleToggleMark = async (markTypeId) => {
    if (userMarks[markTypeId]) {
      await client.delete(`/items/${id}/marks/${markTypeId}`);
      setUserMarks((prev) => { const n = { ...prev }; delete n[markTypeId]; return n; });
    } else {
      const mt = markTypes.find((m) => m.id === markTypeId);
      if (mt?.mutex_group) {
        const hasConflict = markTypes.some(
          (m) => m.mutex_group === mt.mutex_group && m.id !== markTypeId && !!userMarks[m.id]
        );
        if (hasConflict) return;
      }
      const res = await client.post(`/items/${id}/marks`, { mark_type_id: markTypeId });
      setUserMarks((prev) => ({ ...prev, [markTypeId]: res.data.id }));
    }
  };

  const handleArchive = async () => {
    try {
      const endpoint = item.is_archived ? `/items/${id}/unarchive` : `/items/${id}/archive`;
      const response = await client.post(endpoint);
      setItem(response.data);
    } catch {
      alert('Не удалось обновить');
    }
  };

  const handleDelete = async () => {
    if (!confirm('Удалить желание? Это действие нельзя отменить.')) return;
    try {
      await client.delete(`/items/${id}`);
      navigate(-1);
    } catch {
      alert('Не удалось удалить');
    }
  };

  const tags = Array.isArray(item?.tags) ? item.tags.filter(Boolean) : [];
  const marks = Array.isArray(item?.marks) ? item.marks.filter(Boolean) : [];

  if (loading) {
    return (
      <Layout title="Желание" showBack>
        <div style={styles.loadingText}>Загрузка...</div>
      </Layout>
    );
  }

  if (error || !item) {
    return (
      <Layout title="Ошибка" showBack>
        <div style={{ padding: '24px', color: '#ff3b30', textAlign: 'center' }}>
          {error || 'Желание не найдено'}
        </div>
      </Layout>
    );
  }

  const hostname = (() => {
    try {
      return new URL(item.url).hostname.replace(/^www\./, '');
    } catch {
      return item.url;
    }
  })();

  const handleBack = () => {
    const listId = backListId || item?.list_id;
    const categoryId = backCategoryId || item?.category_id;
    if (listId) {
      const params = categoryId ? `?category_id=${categoryId}` : '';
      navigate(`/lists/${listId}${params}`);
    } else {
      navigate(-1);
    }
  };

  return (
    <Layout title={item.title || hostname} showBack onBack={handleBack}>
      <div style={styles.container}>
        {item.image_url ? (
          <img
            src={item.image_url}
            alt={item.title || 'Изображение'}
            style={styles.image}
            onError={(e) => (e.target.style.display = 'none')}
          />
        ) : (
          <div style={styles.imagePlaceholder}>🎁</div>
        )}

        <div style={styles.body}>
          {editingTitle ? (
            <input
              autoFocus
              style={{ ...styles.title, border: '1px solid var(--tg-theme-hint-color, #ddd)', borderRadius: '8px', padding: '4px 8px', background: '#FFFFFF', outline: 'none', width: '100%', boxSizing: 'border-box' }}
              value={titleValue}
              onChange={(e) => setTitleValue(e.target.value)}
              onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') handleSaveTitle(); if (e.key === 'Escape') setEditingTitle(false); }}
              onBlur={handleSaveTitle}
              maxLength={500}
            />
          ) : (
            <div
              style={{ ...styles.title, cursor: 'text', borderBottom: '1px dashed var(--tg-theme-hint-color, #ccc)' }}
              onClick={() => { setTitleValue(item.title || ''); setEditingTitle(true); }}
            >
              {item.title || 'Без названия'}
            </div>
          )}

          {item.url && (
            <a href={item.url} target="_blank" rel="noopener noreferrer" style={styles.urlLink}>
              {hostname}
            </a>
          )}

          {item.description && (
            <p style={styles.description}>{item.description}</p>
          )}

          <div style={styles.section}>
            <div style={styles.sectionTitle}>Заметка</div>
            {editingNotes ? (
              <textarea
                autoFocus
                style={{ ...styles.notes, width: '100%', boxSizing: 'border-box', border: '1px solid var(--tg-theme-hint-color, #ddd)', outline: 'none', resize: 'vertical', fontFamily: 'inherit', fontSize: '14px' }}
                value={notesValue}
                onChange={(e) => setNotesValue(e.target.value)}
                onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Escape') setEditingNotes(false); }}
                onBlur={handleSaveNotes}
                maxLength={2000}
              />
            ) : (
              <div
                style={{ ...styles.notes, cursor: 'text', minHeight: '40px' }}
                onClick={() => { setNotesValue(item.notes || ''); setEditingNotes(true); }}
              >
                {item.notes || <span style={{ color: 'var(--tg-theme-hint-color, #8e8e93)' }}>Добавить заметку...</span>}
              </div>
            )}
          </div>

          <div style={styles.section}>
            <div style={styles.sectionTitle}>Цена</div>
            <PriceField item={item} itemId={id} onUpdate={(price) => setItem((prev) => ({ ...prev, price }))} />
          </div>

          {tags.length > 0 && (
            <div style={styles.section}>
              <div style={styles.sectionTitle}>Теги</div>
              <div style={styles.tags}>
                {tags.map((tag) => (
                  <span key={tag.id} style={styles.tag}>
                    #{tag.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {marks.length > 0 && (
            <div style={styles.section}>
              <div style={styles.sectionTitle}>Реакции</div>
              <div style={styles.marks}>
                {marks.map((mark) => (
                  <div key={mark.id} style={styles.mark}>
                    <span>{mark.mark_emoji || '✓'}</span>
                    <span>{mark.mark_name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {markTypes.length > 0 && (
          <div style={{ padding: '0 16px 16px' }}>
            <div style={{ ...styles.sectionTitle, marginBottom: '10px' }}>Отметить</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {markTypes.map((mt) => {
                const active = !!userMarks[mt.id];
                const blocked = !active && mt.mutex_group && markTypes.some(
                  (m) => m.mutex_group === mt.mutex_group && m.id !== mt.id && !!userMarks[m.id]
                );
                return (
                  <button
                    key={mt.id}
                    style={{ ...styles.markBtn, ...(active ? styles.markBtnActive : {}), ...(blocked ? { opacity: 0.35 } : {}) }}
                    onClick={() => handleToggleMark(mt.id)}
                  >
                    <span>{mt.emoji}</span>
                    <span>{mt.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {categories.length > 1 && (
          <div style={{ padding: '0 16px 16px' }}>
            <div style={{ ...styles.sectionTitle, marginBottom: '8px' }}>Категория</div>
            {movingCategory ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleMoveCategory(cat.id)}
                    style={{
                      padding: '6px 14px', borderRadius: '20px', border: 'none', fontSize: '13px', cursor: 'pointer',
                      background: cat.id === item.category_id ? 'linear-gradient(135deg, #F05A35, #F4956A)' : '#FFFFFF',
                      color: cat.id === item.category_id ? '#fff' : '#2D2424',
                    }}
                  >
                    {cat.title}
                  </button>
                ))}
                <button onClick={() => setMovingCategory(false)} style={{ padding: '6px 14px', borderRadius: '20px', border: '1px solid #C4A99A', background: 'transparent', fontSize: '13px', cursor: 'pointer', color: '#C4A99A' }}>
                  Отмена
                </button>
              </div>
            ) : (
              <button
                onClick={() => setMovingCategory(true)}
                style={{ padding: '6px 14px', borderRadius: '20px', border: '1px solid #F4956A', background: 'transparent', fontSize: '13px', cursor: 'pointer', color: '#F05A35' }}
              >
                {categories.find((c) => c.id === item.category_id)?.title || 'Без категории'} ✎
              </button>
            )}
          </div>
        )}

        <div style={styles.actions}>
          {item.url && (
            <a href={item.url} target="_blank" rel="noopener noreferrer" className="btn-primary" style={styles.openBtn}>
              Открыть ссылку
            </a>
          )}
          {canEdit && (
            <button style={styles.archiveBtn} onClick={handleArchive}>
              {item.is_archived ? '↩ Восстановить' : '📦 В архив'}
            </button>
          )}
        </div>

        {canEdit && (
          <button style={styles.deleteBtn} onClick={handleDelete}>
            Удалить желание
          </button>
        )}

        <div style={styles.meta}>
          Добавил(а) {item.creator_first_name || item.creator_username || 'неизвестно'} •{' '}
          {new Date(item.created_at).toLocaleDateString()}
          {item.category_title && ` • ${item.category_title}`}
        </div>
      </div>
    </Layout>
  );
}
